import api from '../api.js';
import db from '../../versa/db.js';
import crud from '../../versa/crud.js';
import router from '../../versa/router.js';
import { notify, ask } from '../../versa/msgbox.js';
import { parse } from '../../versa/misc.js';
import { translate, resolveFilterLabelsToIds, validateResolvedFilterValues } from '../../versa/filter.js';
import view from '../views/input-docks.js';
import form from '../forms/input-docks.js';
import * as types from '../types.js';
import opt_input_dock_type from '../types/opt_input_dock_type.js';

/**
 * Extrai class_id do filtro Versa e devolve cópia sem essas condições
 * (filtro de classe usa query param + EXISTS no backend).
 * @param {object|null} data
 * @returns {{ filterData: object|null, classId: number|null }}
 */
function splitClassFilter(data)
{
	if (!data || typeof data !== 'object')
		return { filterData: data, classId: null };
	const top = Object.keys(data)
		.filter((k) => /^field-\d+$/.test(k))
		.map((k) => Number(k.slice(6)))
		.reduce((m, n) => (Number.isFinite(n) && n > m ? n : m), -1) + 1;
	const keep = [];
	let classId = null;
	for (let i = 0; i < top; i++)
	{
		const field = data[`field-${i}`];
		if (!field)
			continue;
		if (field === 'class_id')
		{
			const raw = data[`value-${i}`];
			if (raw != null && String(raw).trim() !== '')
			{
				const text = String(raw).trim();
				// classes: id numérico, "company:id" ou JSON [company, id]
				let n = null;
				if (/^\d+$/.test(text))
					n = Number(text);
				else if (/^.+:\d+$/.test(text))
					n = Number(text.split(':').pop());
				else
				{
					try
					{
						const parsed = JSON.parse(text);
						if (Array.isArray(parsed) && parsed.length >= 2)
							n = Number(parsed[1]);
					}
					catch { /* ignore */ }
				}
				if (Number.isFinite(n) && classId == null)
					classId = n;
			}
			continue;
		}
		keep.push(i);
	}
	if (keep.length === top && classId == null)
		return { filterData: data, classId: null };
	const out = {};
	keep.forEach((src, dst) =>
	{
		['open', 'field', 'operator', 'value', 'close', 'connector'].forEach((p) =>
		{
			const key = `${p}-${src}`;
			if (Object.prototype.hasOwnProperty.call(data, key))
				out[`${p}-${dst}`] = data[key];
		});
	});
	return { filterData: Object.keys(out).length ? out : null, classId };
}

const inputDocks = function ()
{
	const rt_crud = crud.create({
		async addFilter(params)
		{
			let f = this.filter;
			if (f && Object.keys(f).some((k) => /^field-\d+$/.test(k)))
			{
				f = await resolveFilterLabelsToIds(f, this.fieldspecs);
				validateResolvedFilterValues(f, this.fieldspecs);
			}
			const { filterData, classId } = splitClassFilter(f);
			const filter = translate(filterData, null, this.fieldspecs);
			if (filter)
				params.append('filter', filter);
			if (classId != null)
				params.append('class_id', String(classId));
			if (f && JSON.stringify(f).includes('is_disabled'))
				params.append('include_disabled', '1');
		},
		async loadBaseData()
		{
			api.call(api.list_companies, this.queryAll()).then((data) => db.set('companies', data).catch((e) => notify(e)));
			api.call(api.list_warehouses, this.queryAll()).then((data) => db.set('warehouses', data).catch((e) => notify(e)));
			api.call(api.list_classes, this.queryAll()).then((data) => db.set('classes', data).catch((e) => notify(e)));
		},
		async queryData(query)
		{
			return await api.call(api.list_input_docks, query);
		},
		async loadRecord(id)
		{
			const data = await api.call(api.select_input_dock, ...JSON.parse(id));
			if (data && Array.isArray(data.classes))
			{
				data.classes = data.classes.map((c) => (
					typeof c === 'object'
						? c
						: [data.company_id, Number(c)]
				));
			}
			return data;
		},
		async saveRecord(data, old_data)
		{
			const parsedClasses = parse(data.classes);
			const classes = Array.isArray(parsedClasses)
				? parsedClasses.map((entry) => Number(Array.isArray(entry) ? entry[1] : (entry?.id ?? entry)))
					.filter((n) => Number.isFinite(n))
				: [];
			let working_hours = data.working_hours;
			if (typeof working_hours === 'string')
			{
				try { working_hours = JSON.parse(working_hours); } catch { working_hours = []; }
			}
			const _record = {
				warehouse_id: Number(data.warehouse_id),
				name: String(data.name || '').trim(),
				dock_type: String(data.dock_type || '').trim(),
				notes: data.notes ? String(data.notes).trim() : null,
				is_disabled: data.is_disabled === true || data.is_disabled === '1' || data.is_disabled === 1,
				classes,
				working_hours: Array.isArray(working_hours) ? working_hours : [],
			};
			if (!_record.name) return notify('Informe o nome da doca.', 'Docas Entrada');
			if (!_record.warehouse_id) return notify('Informe o armazém.', 'Docas Entrada');
			if (!_record.dock_type) return notify('Informe o tipo da doca.', 'Docas Entrada');
			if (old_data)
				return await api.call(api.update_input_dock, data.company_id, data.id, _record);
			const { is_disabled, ...insertBody } = _record;
			if (is_disabled) insertBody.is_disabled = true;
			return await api.call(api.insert_input_dock, data.company_id, insertBody);
		},
		async setDisabled(disabled)
		{
			const ids = await this.getSelectedIds(['company_id', 'id']);
			if (!ids.length) return notify('Selecione ao menos uma doca.', 'Docas Entrada');
			const action = disabled ? 'Inativar' : 'Ativar';
			ask(`Confirma ${action} ${ids.length} doca(s)?`, action, async (answer) =>
			{
				if (answer !== 'Y') return;
				for (const [company_id, id] of ids)
				{
					const resp = await api.call(api.patch_input_dock_disabled, company_id, id, { is_disabled: !!disabled });
					if (resp == null) { notify('Falha ao atualizar doca.', 'Docas Entrada'); return; }
				}
				this.loadPage();
			});
		},
	});

	router.set('#input-dock-disable', () => rt_crud.setDisabled(true));
	router.set('#input-dock-enable', () => rt_crud.setDisabled(false));

	// Ordem = colunas da grade (views/input-docks.js). class_id só no filtro.
	const fieldspecs = [
		{ index: 1, field: 'company_id', label: 'Empresa', types: [types.datalist()], placeholder: '', title: 'Empresa', store: 'companies', labels: ['nick'] },
		{ index: 2, field: 'warehouse_id', label: 'Armazém', types: [types.datalist()], placeholder: '', title: 'Armazém', store: 'warehouses', labels: ['name'] },
		{ index: 3, field: 'name', label: 'Nome', types: [], placeholder: '', title: 'Nome da doca' },
		{ index: 4, field: 'dock_type', label: 'Tipo', types: [opt_input_dock_type(), types.datalist()], placeholder: '', title: 'Tipo da doca' },
		{ index: 5, field: 'classes_label', label: 'Classes', types: [], placeholder: '', title: 'Classes aceitas (resumo)', computed: true },
		{ index: 6, field: 'working_hours_summary', label: 'Horário', types: [], placeholder: '', title: 'Resumo de horários', computed: true },
		{ index: 7, field: 'is_disabled', label: 'Status', types: [types.opt_bool(), types.datalist()], placeholder: '', title: 'Inativa (Sim=Inativa / Não=Ativa)' },
		{ index: 8, field: 'class_id', label: 'Classe', types: [types.datalist()], placeholder: '', title: 'Filtrar docas que aceitam esta classe', store: 'classes', labels: ['name'] },
	];

	const operations = [
		{ hash: '#input-dock-disable', label: 'Inativar' },
		{ hash: '#input-dock-enable', label: 'Ativar' },
	];

	rt_crud.run(
		'input_docks',
		'Docas&nbsp;Entrada',
		document.getElementById('workarea'),
		view,
		form,
		fieldspecs,
		operations
	);
};

export default inputDocks;
